import { TOTAL_COLUMNS } from "../../../components/dashboard/DashboardCustomization/constants";

import type { WebSection } from "byzantine/src/dbbl/businessLogic/modules/userDisplayPreferences";

export const getSectionKey = (items: WebSection[]): string =>
  items.map(({ section_id }) => section_id).join("-");

export const getFormattedSectionKey = (
  section: WebSection | WebSection[]
): string => {
  if (section instanceof Array) {
    return getSectionKey(section);
  }

  return section.section_id;
};

export const isSectionHalfRow = (section: WebSection[]): boolean =>
  !!section.find((item) => item.section_id === "blank");

export const withoutBlankSections = (sections: WebSection[]) =>
  sections.filter(({ section_id }) => section_id !== "blank");

export const flattenRowsForReorder = (
  rows: (WebSection | WebSection[])[]
): WebSection[] => {
  const isOddNumberOfHalfRows = rows.reduce(
    (acc, row) =>
      row instanceof Array
        ? acc || row.some(({ section_id }) => section_id === "blank")
        : acc,
    false
  );
  const lastHalfRowIndex = rows.reduce(
    (lastIndex, row, index) => (row instanceof Array ? index : lastIndex),
    -1
  );

  if (
    lastHalfRowIndex === -1 ||
    lastHalfRowIndex === rows.length - 1 ||
    !isOddNumberOfHalfRows
  ) {
    return withoutBlankSections(rows.flat());
  }

  // If we have a half row that is not the last row, we need to move it to
  // the end of the list.
  const reorderedRows = [...rows];
  const [lastHalfRow] = reorderedRows.splice(lastHalfRowIndex, 1);
  reorderedRows.push(lastHalfRow);

  return withoutBlankSections(reorderedRows.flat());
};

export const formatRowsForRender = (
  values: WebSection[],
  getRowSize: (sectionId: string) => number
): (WebSection | WebSection[])[] => {
  const blankSection: WebSection = { section_id: "blank" };

  const { activeGroup, activeGroupIndex, result } = values
    .filter(({ section_id }) => section_id !== "blank")
    .reduce(
      (acc, value) => {
        const rowSize = getRowSize(value.section_id);

        // If you have a full row
        if (rowSize === TOTAL_COLUMNS) {
          return {
            activeGroup: acc.activeGroup,
            activeGroupIndex: acc.activeGroupIndex,
            result: [...acc.result, value],
          };
        }

        // If you have a half row and an existing half row
        if (acc.activeGroupIndex !== null) {
          const resultCopy = [...acc.result];
          resultCopy[acc.activeGroupIndex] = [acc.activeGroup[0], value];

          return {
            activeGroup: [],
            activeGroupIndex: null,
            result: resultCopy,
          };
        }

        // If you have a half row and no existing half row
        const activeGroupCopy = [value];

        return {
          activeGroup: activeGroupCopy,
          activeGroupIndex: acc.result.length,
          result: [...acc.result, activeGroupCopy],
        };
      },
      {
        result: [] as (WebSection | WebSection[])[],
        activeGroup: [] as WebSection[],
        activeGroupIndex: null as number | null,
      }
    );

  if (activeGroupIndex === null) {
    return result;
  }

  const formattedResult = [...result];
  formattedResult[activeGroupIndex] = [activeGroup[0], blankSection];
  return formattedResult;
};
